import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { mapProps } from 'recompose';
import uniqBy from 'lodash.uniqby';

import IdentityTagSelection from './components/IdentityTagSelection';
import { AlbumFolderTags } from '../../../../../graphql/queries/album-folder-tags';
import { getAccountAlbumFolderSubscribedIdentities } from './graphql/queries/getAccountAlbumFolderSubscribedIdentities';
import IdentityTagSelectionHeader from './components/IdentityTagSelectionHeader';
import IdentityTagSelectionSuccessModal from './components/IdentityTagSelectionSuccessModal';
import TagSelectionNullState from './components/TagSelectionNullState';
import GradeTagSelection from './components/GradeTagSelection';
import { isAlbumFolderGradeTagSelectionRequired } from './helpers';
import Alert from '../../../../../components/global/Alert';
import { getTagsByType } from '../../../../../util/album-folder-tags';
import { ALBUM_FOLDER_TAG_TYPE } from '../../../../../constants/album-folder-tags';
import OnboardingLayout from '../OnboardingLayout/OnboardingLayout';
import OnboardingLayoutScrollableContent from '../OnboardingLayout/components/OnboardingLayoutScrollableContent';
import { getPurchasedIdentitiesOnFolder } from '../../../../../util/account';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  identities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ),
  targetIdentityId: PropTypes.string,
  loadingIdentities: PropTypes.bool,
};

const TagSelection = ({
  albumFolder,
  identities,
  targetIdentityId,
  loadingIdentities,
}) => {
  const initialIdentityId =
    identities.find(({ id }) => id === targetIdentityId)?.id ??
    identities[0]?.id;

  const [activeIdentityId, setActiveIdentityId] = useState(initialIdentityId);
  const [gradeTagSetupComplete, setGradeTagSetupComplete] = useState(false);
  const [uniformTagSetupComplete, setUniformTagSetupComplete] = useState(false);

  const activeIdentity = identities.find(({ id }) => id === activeIdentityId);

  const getNextIdentity = () => {
    // If we have a target identity, limit the tag selection only to that
    // particular identity.
    if (targetIdentityId) {
      return undefined;
    }

    const identityIndex = identities.findIndex(
      ({ id }) => id === activeIdentityId,
    );
    return identities[identityIndex + 1];
  };

  useEffect(() => {
    if (!activeIdentityId && initialIdentityId) {
      setActiveIdentityId(initialIdentityId);
    }
  }, [initialIdentityId]);

  if (!albumFolder || loadingIdentities) {
    return null;
  }

  // Extra safe in case a wrong URL was send and the user
  // is not subscribed.
  if (!activeIdentity || !albumFolder.tags?.records?.length) {
    return (
      <OnboardingLayout>
        <Helmet>
          <title>Sports &amp; Activities info &ndash; Waldo Photos</title>
        </Helmet>
        <TagSelectionNullState albumFolder={albumFolder} />
      </OnboardingLayout>
    );
  }

  const onUniformTagSetupComplete = () => {
    setUniformTagSetupComplete(true);
  };

  const onGradeTagSetupComplete = () => {
    setGradeTagSetupComplete(true);
  };

  const hasUniformTags =
    getTagsByType(ALBUM_FOLDER_TAG_TYPE.UNIFORM, albumFolder.tags.records)
      .length > 0;

  const isSetupComplete = () => {
    return (
      (!hasUniformTags || uniformTagSetupComplete) &&
      (!isAlbumFolderGradeTagSelectionRequired(albumFolder) ||
        gradeTagSetupComplete)
    );
  };

  return (
    <OnboardingLayout>
      <Helmet>
        <title>Sports info &ndash; Waldo Photos</title>
      </Helmet>

      <div className="d-flex flex-column flex-auto w-100 h-100">
        <IdentityTagSelectionHeader
          identity={activeIdentity}
          albumFolder={albumFolder}
        />
        {!isSetupComplete() && (
          <Alert type="info" className="my-2">
            <p>Please complete the following steps:</p>
            <ul>
              {!uniformTagSetupComplete && (
                <li>Setup team &amp; sports information</li>
              )}
              {!gradeTagSetupComplete &&
                isAlbumFolderGradeTagSelectionRequired(albumFolder) && (
                  <li>Setup grade</li>
                )}
            </ul>
          </Alert>
        )}

        <OnboardingLayoutScrollableContent>
          <IdentityTagSelection
            // Force fetching clean tag data for each identity
            // when cycling.
            key={activeIdentity?.id}
            albumFolder={albumFolder}
            identity={activeIdentity}
            className="mb-6 mb-md-0"
            onComplete={onUniformTagSetupComplete}
          />
          {isAlbumFolderGradeTagSelectionRequired(albumFolder) && (
            <div className="py-2">
              <h3>Set up grade</h3>
              <GradeTagSelection
                // Force fetching clean tag data for each identity
                // when cycling.
                key={activeIdentity?.id}
                albumFolder={albumFolder}
                identity={activeIdentity}
                onComplete={onGradeTagSetupComplete}
                completeBtnText="Save"
              />
            </div>
          )}
        </OnboardingLayoutScrollableContent>

        <IdentityTagSelectionSuccessModal
          forceOpen={isSetupComplete()}
          identity={activeIdentity}
          nextIdentity={getNextIdentity()}
          onNext={() => {
            setActiveIdentityId(getNextIdentity()?.id);
            setGradeTagSetupComplete(false);
            setUniformTagSetupComplete(false);
          }}
          onDismiss={() => {
            setGradeTagSetupComplete(false);
            setUniformTagSetupComplete(false);
          }}
        />
      </div>
    </OnboardingLayout>
  );
};

TagSelection.propTypes = propTypes;

const withAlbumFolderTags = graphql(AlbumFolderTags, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder } = {} }) => ({
    albumFolder,
  }),
});

export { TagSelection as TagSelectionComponent };

export default compose(
  withRouter,
  withAlbumFolderTags,
  mapProps(({ match, ...props }) => ({
    ...props,
    match,
    targetIdentityId: match.params.targetIdentityId,
    albumFolderId: match.params.albumFolderId,
  })),
  getAccountAlbumFolderSubscribedIdentities,
  mapProps(({ data, albumFolderId, ...props }) => ({
    ...props,
    loadingIdentities: data?.loading,
    identities: uniqBy(
      [
        ...(data?.me?.subscribed_album_folder_identities?.records ?? []).map(
          subscribedIdentity => subscribedIdentity.identity,
        ),
        ...getPurchasedIdentitiesOnFolder(data?.me, albumFolderId),
      ],
      ({ id }) => id,
    ),
  })),
)(TagSelection);
